<!-- LabelSheet — reading the bag you picked up off the wall.

     Phone: an Ionic sheet at 60%, draggable to full. Below full the backdrop is
     off, so the lifted bag above the sheet stays live and can be turned; a tap
     on the scene is the Shelf's to handle (it puts the bag back).
     It drags like a native sheet: a swipe up anywhere on it (the label included)
     expands it, and the label only scrolls once it is fully up (Ionic's
     expandToScroll). Fully up, it stops below the status bar: Ionic's own sheet
     height, calc(100% - safe-area-top - 10px). The handle cycles the stops.
     The Add-to-bag rail stays pinned to the screen's foot at every stop (pinRail).
     ≥ 900px wide: a 400px panel on the right, with the wall still visible and
     walkable beside it.

     Props/events are the Shelf's contract: `coffee` (object|null) opens it;
     `close`, `prev`, `next`, `ar` go back up. Focus is held inside while open
     and returned to where it came from on close. -->
<template>
  <!-- Phone -->
  <IonModal
    v-if="!wide"
    ref="modal"
    :is-open="!!coffee"
    :breakpoints="[0, 0.68, 1]"
    :initial-breakpoint="0.68"
    :backdrop-breakpoint="0.6"
    :handle="true"
    handle-behavior="cycle"
    :focus-trap="false"
    class="label-sheet"
    aria-label="Label"
    @did-present="onPresent"
    @did-dismiss="onDismiss"
  >
    <IonContent v-if="shown" class="label-sheet__content">
      <div ref="box" class="box" tabindex="-1" :aria-label="`Label: ${shown.name}`">
        <div class="bar">
          <div class="bar__steps">
            <button type="button" class="bar__btn" aria-label="Previous coffee" @click="emit('prev')">
              <IonIcon :icon="chevronBack" aria-hidden="true" />
            </button>
            <button type="button" class="bar__btn" aria-label="Next coffee" @click="emit('next')">
              <IonIcon :icon="chevronForward" aria-hidden="true" />
            </button>
          </div>
          <button type="button" class="bar__btn bar__btn--close" aria-label="Close label" @click="close">
            <IonIcon :icon="closeOutline" aria-hidden="true" />
          </button>
        </div>

        <BagHero :coffee="shown" :size="size" />
        <div class="body">
          <CoffeeLabel :coffee="shown" @navigate="close">
            <template #buy>
              <SizePicker v-model:size="size" v-model:qty="qty" :offers="shown.offers" />
            </template>
          </CoffeeLabel>
          <button type="button" class="ar" @click="emit('ar', size)">
            <IonIcon :icon="cubeOutline" aria-hidden="true" />
            See it in your room
          </button>
        </div>
      </div>
    </IonContent>
    <IonFooter v-if="shown" class="ion-no-border label-sheet__footer">
      <ActionRail>
        <IonButton
          expand="block"
          class="add"
          :class="{ 'add--done': added }"
          :disabled="soldOut || add.busy.value"
          @click="add.run()"
        >
          {{ label }}
        </IonButton>
        <p v-if="add.error.value" class="field-error" role="alert">{{ add.error.value }}</p>
        <p v-else-if="soldOut" class="rail-note">Every size is sold out on BeanHub right now.</p>
      </ActionRail>
    </IonFooter>
  </IonModal>

  <!-- Tablet / desktop -->
  <Transition v-else appear :name="reducedMotion ? 'panel-fade' : 'panel'" @after-enter="onPresent" @after-leave="onPanelGone">
    <aside
      v-if="coffee && shown"
      ref="box"
      class="panel"
      role="dialog"
      aria-modal="false"
      :aria-label="`Label: ${shown.name}`"
      tabindex="-1"
    >
      <div class="bar">
        <div class="bar__steps">
          <button type="button" class="bar__btn" aria-label="Previous coffee" @click="emit('prev')">
            <IonIcon :icon="chevronBack" aria-hidden="true" />
          </button>
          <button type="button" class="bar__btn" aria-label="Next coffee" @click="emit('next')">
            <IonIcon :icon="chevronForward" aria-hidden="true" />
          </button>
        </div>
        <button type="button" class="bar__btn bar__btn--close" aria-label="Close label" @click="close">
          <IonIcon :icon="closeOutline" aria-hidden="true" />
        </button>
      </div>

      <div class="panel__scroll">
        <BagHero :coffee="shown" :size="size" />
        <div class="body">
          <CoffeeLabel :coffee="shown" @navigate="close">
            <template #buy>
              <SizePicker v-model:size="size" v-model:qty="qty" :offers="shown.offers" />
            </template>
          </CoffeeLabel>
          <button type="button" class="ar" @click="emit('ar', size)">
            <IonIcon :icon="cubeOutline" aria-hidden="true" />
            See it in your room
          </button>
        </div>
      </div>

      <div class="panel__rail">
        <IonButton
          expand="block"
          class="add"
          :class="{ 'add--done': added }"
          :disabled="soldOut || add.busy.value"
          @click="add.run()"
        >
          {{ label }}
        </IonButton>
        <p v-if="add.error.value" class="field-error" role="alert">{{ add.error.value }}</p>
        <p v-else-if="soldOut" class="rail-note">Every size is sold out on BeanHub right now.</p>
      </div>
    </aside>
  </Transition>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { IonModal, IonContent, IonFooter, IonButton, IonIcon } from '@ionic/vue';
import { chevronBack, chevronForward, closeOutline, cubeOutline } from 'ionicons/icons';
import ActionRail from '../core/ui/ActionRail.vue';
import { trapFocus } from '../core/a11y.js';
import { reducedMotion } from '../store.js';
import CoffeeLabel from './CoffeeLabel.vue';
import BagHero from './BagHero.vue';
import SizePicker, { useAddToBag } from './SizePicker.vue';

const props = defineProps({
  coffee: { type: Object, default: null },
});
const emit = defineEmits(['close', 'prev', 'next', 'ar']);

// What the sheet shows. Held through the close animation, so the content does
// not blank out while the sheet is still sliding away.
const shown = ref(props.coffee);
watch(() => props.coffee, (c) => { if (c) shown.value = c; });

const { size, qty, soldOut, added, add, label } = useAddToBag(shown);

// --- phone ↔ panel ------------------------------------------------------------
const mq = typeof matchMedia === 'function' ? matchMedia('(min-width: 56.25em)') : null; // 900px
const wide = ref(!!mq?.matches);
const onMq = (e) => { wide.value = e.matches; };
onMounted(() => mq?.addEventListener?.('change', onMq));
onBeforeUnmount(() => { mq?.removeEventListener?.('change', onMq); release(); });

// --- focus ------------------------------------------------------------------
const box = ref(null);
const modal = ref(null);
let releaseTrap = null;
function release () { releaseTrap?.(); releaseTrap = null; }

// --- the rail, pinned to the foot of the screen ---------------------------------
//
// With expandToScroll the whole sheet slides (the label is not a separately
// scrolling box below full height), so a footer in it would sit off screen at
// 60%. While the sheet is moving (present, drag, snap, handle, dismiss) the rail
// is lifted by however much of the sheet is below the screen, frame by frame.
let railUntil = 0;
let railRaf = 0;
function pinRail () {
  const el = modal.value?.$el;
  const wr = el?.shadowRoot?.querySelector('.modal-wrapper');
  const ft = el?.querySelector('ion-footer');
  if (!wr || !ft) return;
  const r = wr.getBoundingClientRect();
  const below = r.bottom - el.getBoundingClientRect().bottom;
  // Lift by what is below the screen, but never above the sheet's own top edge.
  const lift = Math.max(0, Math.min(below, r.height - ft.offsetHeight));
  const t = lift > 0.5 ? `translate3d(0, ${(-lift).toFixed(1)}px, 0)` : '';
  if (ft.style.transform !== t) ft.style.transform = t;
}
function trackRail (ms) {
  railUntil = Math.max(railUntil, window.performance.now() + ms);
  if (railRaf) return;
  const tick = () => {
    pinRail();
    railRaf = window.performance.now() < railUntil ? requestAnimationFrame(tick) : 0;
  };
  railRaf = requestAnimationFrame(tick);
}
const RAIL_EVENTS = {
  ionModalWillPresent: 900,
  ionModalDidPresent: 120,
  ionDragStart: 30000,          // held until the drag ends
  ionDragMove: 700,
  ionDragEnd: 900,
  ionBreakpointDidChange: 700,
  ionModalWillDismiss: 900,
  click: 900,                   // the handle cycling the stops
};
const railListeners = Object.entries(RAIL_EVENTS).map(([type, ms]) => [type, () => {
  if (type === 'ionDragEnd') railUntil = 0;
  trackRail(ms);
}]);
watch(modal, (m, old) => {
  const prev = old?.$el; const el = m?.$el;
  railListeners.forEach(([type, fn]) => { prev?.removeEventListener(type, fn); el?.addEventListener(type, fn); });
});
onBeforeUnmount(() => {
  cancelAnimationFrame(railRaf);
  const el = modal.value?.$el;
  railListeners.forEach(([type, fn]) => el?.removeEventListener(type, fn));
});


async function onPresent () {
  await nextTick();
  // The phone sheet's rail lives in its footer, outside the scrolling box, so
  // the trap wraps the whole modal there.
  const el = wide.value ? box.value : (modal.value?.$el || box.value);
  if (!el) return;
  release();
  releaseTrap = trapFocus(el);
  // Land on the label itself, not a control: a screen reader reads the
  // coffee's name, and a finger-driven phone does not get a focus ring on ✕
  // every time a bag is picked up. Tab goes on from there.
  (box.value || el).focus({ preventScroll: true });
}

// Escape on the panel, the ✕, or a roaster link: ask the Shelf to put it back.
function close () {
  if (props.coffee) emit('close');
}

// The sheet was swiped away or Esc'd by Ionic. If the Shelf closed it by
// setting coffee to null, it already knows, so say nothing.
function onDismiss () {
  release();
  if (props.coffee) emit('close');
}
function onPanelGone () { release(); }
watch(() => props.coffee, (c) => { if (!c && wide.value) release(); });

// Esc puts the bag back from anywhere on the page while the panel is open —
// the wall beside it stays live, so focus may well be out there.
function onWindowKey (e) {
  if (e.key === 'Escape' && wide.value && props.coffee) close();
}
onMounted(() => window.addEventListener('keydown', onWindowKey));
onBeforeUnmount(() => window.removeEventListener('keydown', onWindowKey));
</script>

<style scoped>
.box { min-height: 100%; outline: none; background: var(--dock, #FAF7F1); }

.bar {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--sp-3) var(--sp-3) var(--sp-3);
  background: var(--dock, #FAF7F1);
}
.bar__steps { display: flex; gap: var(--sp-2); }
.bar__btn {
  display: grid;
  place-items: center;
  width: var(--tap);
  height: var(--tap);
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--sage, #E6EDE4);
  color: var(--ink, #1F2A22);
  font-size: var(--text-xl);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform 120ms var(--ease), background var(--dur) var(--ease);
}
.bar__btn:hover { background: color-mix(in srgb, var(--sage, #E6EDE4) 80%, var(--ink, #1F2A22)); }
.bar__btn:active { transform: scale(0.92); }
.bar__btn:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: 2px; }

.body {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
  padding: var(--sp-4) var(--gutter) var(--sp-6);
}

.ar {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  min-height: var(--tap);
  padding: 0 var(--sp-4) 0 var(--sp-3);
  border: 1px solid var(--line, #ECE6DC);
  border-radius: 999px;
  background: var(--dock, #FAF7F1);
  color: var(--ink, #1F2A22);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 700;
  cursor: pointer;
  transition: background var(--dur) var(--ease);
}
.ar ion-icon { font-size: var(--text-lg); }
.ar:hover { background: var(--sage, #E6EDE4); }
.ar:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: 2px; }

/* The one primary: ink pill, 52 tall, dock text. */
.add {
  --background: var(--ink, #1F2A22);
  --background-hover: var(--ink, #1F2A22);
  --background-activated: var(--ink, #1F2A22);
  --background-focused: var(--ink, #1F2A22);
  --color: var(--dock, #FAF7F1);
  --border-radius: 999px;
  --box-shadow: none;
  height: calc(var(--sp-7) + var(--sp-1));
  margin: 0;
  font-family: var(--font-body, 'Manrope', system-ui, sans-serif);
  font-size: calc(var(--text-md) * 1.0625);
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
  font-variant-numeric: tabular-nums;
  transition: transform 120ms var(--ease);
}
.add:active { transform: scale(0.97); }
@media (prefers-reduced-motion: reduce) { .add, .add:active { transform: none; transition: none; } }
.add--done { --background: var(--leaf, #3F7A4E); --background-activated: var(--leaf, #3F7A4E); --background-hover: var(--leaf, #3F7A4E); }
.add[disabled] { opacity: 0.4; }
.rail-note { margin: 0; color: var(--muted, #5E6B60); font-size: var(--text-sm); text-align: center; }

/* --- the panel (≥ 900px) --- */
.panel {
  position: fixed;
  inset: 0 0 0 auto;
  z-index: 20;
  display: flex;
  flex-direction: column;
  width: min(26rem, 100%);
  background: var(--dock, #FAF7F1);
  border-radius: var(--radius-lg) 0 0 var(--radius-lg);
  box-shadow: -10px 0 40px rgba(40, 30, 20, 0.18), -1px 0 0 var(--line, #ECE6DC);
  outline: none;
}
.panel .bar { padding-top: calc(env(safe-area-inset-top, 0px) + var(--sp-3)); border-radius: var(--radius-lg) 0 0 0; }
.panel__scroll { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.panel__rail {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  padding: var(--sp-3) var(--gutter) calc(var(--sp-4) + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--line, #ECE6DC);
  background: var(--dock, #FAF7F1);
  border-radius: 0 0 0 var(--radius-lg);
}

.panel-enter-active, .panel-leave-active { transition: transform 450ms cubic-bezier(0.2, 0.9, 0.25, 1.05), opacity 200ms linear; }
.panel-enter-from, .panel-leave-to { transform: translateX(100%); opacity: 0.6; }
.panel-fade-enter-active, .panel-fade-leave-active { transition: opacity 150ms linear; }
.panel-fade-enter-from, .panel-fade-leave-to { opacity: 0; }
</style>

<style>
/* The sheet renders at the app root, outside the scoped tree. */
/* No --height here: Ionic's sheet height (100% less the status bar and 10px)
   keeps the top bar clear of the status bar when the sheet is fully up. */
ion-modal.label-sheet {
  --border-radius: 24px;
  --background: var(--dock, #FAF7F1);
  --backdrop-opacity: 0.32;
}
ion-modal.label-sheet::part(content) { box-shadow: 0 -10px 24px rgba(40, 30, 20, 0.14); }
ion-modal.label-sheet::part(handle) { background: color-mix(in srgb, var(--ink, #1F2A22) 22%, transparent); width: 40px; }
ion-modal.label-sheet .label-sheet__content { --background: var(--dock, #FAF7F1); }
ion-modal.label-sheet .label-sheet__footer { position: relative; z-index: 4; will-change: transform; }
ion-modal.label-sheet .label-sheet__footer .action-rail {
  position: static;
  background: var(--dock, #FAF7F1);
  border-top: 1px solid var(--line, #ECE6DC);
}
</style>
